import React from 'react'
import { createPortal } from 'react-dom'
import PropTypes, { ValidationMap, Validator } from 'prop-types'
import cn from 'classnames'

import { useTranslation, withI18nContext } from '@mc/i18n'
import i18nInstance from '../../i18n/i18nInstance'

import { PROP_TYPE_CHILDREN } from '../constants'

export const DIRECTION_PREV = 'prev'
export const DIRECTION_NEXT = 'next'

type ArrowProps = React.PropsWithChildren<{
  className?: string
  direction: 'prev' | 'next'
  location: Element | null
  noBackground?: boolean
  onClick?: React.HTMLProps<HTMLButtonElement>['onClick']
  disabled?: boolean
}>

const Arrow = ({
  children,
  className,
  direction,
  location,
  onClick,
  disabled = false,
  noBackground,
}: ArrowProps) => {
  const { t } = useTranslation('@mc/design-system')

  const ariaLabelDirection =
    direction === DIRECTION_PREV
      ? t('carousel.previousButtonAlt')
      : t('carousel.nextButtonAlt')

  const classes = cn({
    [className ?? '']: className,
    'mc-carousel__arrow': true,
    [`mc-carousel__arrow--${direction}`]: direction,
    'mc-carousel__arrow--no-background': noBackground,
    [`mc-carousel__arrow--no-background--${direction}`]: noBackground,
  })

  if (!location) {
    return null
  }

  return createPortal(
    <button
      className={classes}
      onClick={onClick}
      aria-label={ariaLabelDirection ?? undefined}
      disabled={disabled}
    >
      <span className='mc-carousel__arrow-text'>{children}</span>
    </button>,
    location,
  )
}

Arrow.propTypes = {
  children: PROP_TYPE_CHILDREN,
  className: PropTypes.string,
  direction: PropTypes.oneOf([DIRECTION_PREV, DIRECTION_NEXT])
    .isRequired as Validator<'prev' | 'next'>,
  location:
    typeof Element === 'undefined'
      ? PropTypes.any
      : // This usage of `window` cannot be correctly verified by the eslint rule
        // ssr-friendly/no-dom-globals-in-module-scope. :(
        PropTypes.instanceOf(Element),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  t: PropTypes.func as Validator<(k: string) => string | undefined>,
  noBackground: PropTypes.bool,
} as ValidationMap<ArrowProps>

export default withI18nContext(i18nInstance)(Arrow)
